import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';

export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Enter address",
  className = "" 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search function
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // OpenStreetMap Nominatim API - free, no API key required
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&countrycodes=in&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'JustOffs-App'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          address: item.address || {},
          type: item.type,
          // Extract useful parts
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.state_district || '',
          area: item.address?.suburb || item.address?.neighbourhood || item.address?.county || '',
          shortAddress: formatShortAddress(item)
        })));
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    }
    setIsLoading(false);
  };

  // Format a shorter, cleaner address for display
  const formatShortAddress = (item) => {
    const parts = [];
    const addr = item.address || {};
    
    if (addr.road || addr.street) parts.push(addr.road || addr.street);
    if (addr.suburb || addr.neighbourhood) parts.push(addr.suburb || addr.neighbourhood);
    if (addr.city || addr.town || addr.village) parts.push(addr.city || addr.town || addr.village);
    if (addr.state) parts.push(addr.state);
    
    return parts.length > 0 ? parts.join(', ') : item.display_name.split(',').slice(0, 3).join(', ');
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call by 300ms
    debounceRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    const fullAddress = suggestion.shortAddress || suggestion.display_name;
    onChange(fullAddress);
    setSuggestions([]);
    setShowDropdown(false);

    if (onSelect) {
      onSelect({
        address: fullAddress,
        latitude: suggestion.lat,
        longitude: suggestion.lon,
        city: suggestion.city,
        area: suggestion.area,
        mapsLink: `https://www.google.com/maps/search/?api=1&query=${suggestion.lat},${suggestion.lon}`
      });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value && value.length >= 3 && suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={`w-full px-3 py-2.5 pl-10 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${className}`}
          data-testid="address-autocomplete-input"
          autoComplete="off"
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <MapPin className="w-4 h-4 text-slate-400" />
        </div>
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-slate-400 mt-1">
        Start typing for address suggestions
      </p>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden animate-[fadeIn_0.15s_ease-out]"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                index === selectedIndex 
                  ? 'bg-orange-50' 
                  : 'hover:bg-slate-50'
              } ${index !== suggestions.length - 1 ? 'border-b border-slate-100' : ''}`}
            >
              <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${index === selectedIndex ? 'text-orange-500' : 'text-slate-400'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${index === selectedIndex ? 'text-orange-600' : 'text-slate-700'}`}>
                  {suggestion.shortAddress}
                </p>
                {suggestion.shortAddress !== suggestion.display_name && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    {suggestion.display_name}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && !isLoading && value && value.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-4 text-center">
          <Search className="w-5 h-5 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No addresses found</p>
          <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
        </div>
      )}
    </div>
  );
};
